import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import type { ImageCarouselInterstitial } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import Divider from 'LuxKit/Divider'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineRefreshIcon from 'LuxKit/Icons/line/LineRefreshIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent, {
  StyledModalContent,
} from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import type { RegenerateIndividualItemActivity } from 'tripPlanner/hooks/api/regenerateIndividualItem'
import { useAddBasicActivityToTripMutation } from 'tripPlanner/hooks/api/regenerateIndividualItem'
import { usePlaceImages } from 'tripPlanner/hooks/Google/usePlaceImages'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { cleanUrlString } from 'tripPlanner/utils/explore'
import ExploreReviews from '../../TripModal/ExploreItems/ExploreReviews'
import OpeningHours from '../../TripModal/ExploreItems/OpeningHours'
import RegenerateItemDetailImage from './RegenerateItemDetailImage'

const StyledImageCarousel = styled(ImageCarousel)`
  flex-shrink: 0;
  height: ${rem(200)};

  ${mediaQueryUp.tablet} {
    height: ${rem(240)};
  }

  & + ${StyledModalContent} {
    margin-top: ${rem(20)};

    ${mediaQueryUp.tablet} {
      margin-top: ${rem(24)};
    }
  }
`

interface RegenerateItemDetailsProps {
  itemToReplace: TripItem
  activity: RegenerateIndividualItemActivity
}

function RegenerateItemDetails({
  itemToReplace,
  activity,
}: RegenerateItemDetailsProps) {
  const { closeModal, goBack } = useModalCallbacks()
  const { data: images, isLoading: isImageLoading } = usePlaceImages({
    placeId: activity.googlePlaceDetails.placeId!,
    maxWidthPx: 1000,
    count: 10,
  })
  const { mutate: addActivity } = useAddBasicActivityToTripMutation({
    onSuccess: () => {
      closeModal()
    },
    onError: () => {
      closeModal()
      showSnackbar('Unable to replace the item. Please try again.', 'critical')
    },
  })
  const hasRatingOrReviews = !!(activity.googlePlaceDetails.rating || activity.googlePlaceDetails.userRatingCount)

  const subtitleElements = nonNullable([
    activity.googlePlaceDetails.primaryTypeDisplayName,
    activity.googlePlaceDetails.formattedAddress?.split(', ').at(-1),
  ])

  const website = activity.googlePlaceDetails.websiteUri && cleanUrlString(activity.googlePlaceDetails.websiteUri)

  const interstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (!images || isImageLoading) return undefined

    return nonNullable(images.map((image, index) => {
      if (!image.url || !image.id) {
        return null
      }
      return {
        id: image.id,
        position: index,
        element: <RegenerateItemDetailImage url={image.url} />,
      }
    }))
  }, [images, isImageLoading])

  const handleReplace = useCallback(() => {
    addActivity({
      tripId: itemToReplace.tripId,
      itemId: itemToReplace.id,
      activity: {
        basicActivity: {
          name: activity.basicActivity.name,
          placeName: activity.basicActivity.placeName,
          backUpPlaceName: activity.basicActivity.backUpPlaceName ?? null,
          secondBackUpPlaceName: activity.basicActivity.secondBackUpPlaceName ?? null,
          locationBias: activity.basicActivity.locationBias,
          description: activity.basicActivity.description,
          experienceId: activity.basicActivity.experienceId,
          date: activity.basicActivity.date ?? null,
          startTime: activity.basicActivity.startTime ?? null,
          imageUrl: activity.basicActivity.imageUrl,
          suggestReplanDay: activity.basicActivity.suggestReplanDay,
        },
        googlePlaceDetails: activity.googlePlaceDetails,
      },
    })
  }, [activity.basicActivity, activity.googlePlaceDetails, itemToReplace.id, itemToReplace.tripId, addActivity])

  return (
    <>
      <ModalHeader
        title={activity.basicActivity.name}
        subtitle={subtitleElements.join(' · ')}
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        {images && (
          <StyledImageCarousel
            interstitials={interstitials}
            images={EmptyArray}
            eagerLoadFirstImage
            arrowBehaviour="always-visible"
          />
        )}
        <ModalContent>
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={8}>
              {activity.googlePlaceDetails.formattedAddress && (
                <BodyTextBlock
                  variant="medium"
                  startIcon={<LineMapMarkerIcon />}
                >
                  {activity.googlePlaceDetails.formattedAddress}
                </BodyTextBlock>
              )}
              {activity.googlePlaceDetails.internationalPhoneNumber && (
                <BodyTextBlock variant="medium" startIcon={<LinePhoneIcon />}>
                  {activity.googlePlaceDetails.internationalPhoneNumber}
                </BodyTextBlock>
              )}
              {website && (
                <Clickable
                  href={activity.googlePlaceDetails.websiteUri}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BodyTextBlock
                    variant="medium"
                    startIcon={<LineGlobeIcon />}
                    weight="bold"
                    wrap="word-wrap"
                  >
                    {website}
                  </BodyTextBlock>
                </Clickable>
              )}
              {activity.googlePlaceDetails.openingHoursWeekdayDescriptions && (
                <OpeningHours
                  descriptions={activity.googlePlaceDetails.openingHoursWeekdayDescriptions}
                />
              )}
            </VerticalSpacer>

            <BodyText variant="medium">{activity.googlePlaceDetails.editorialSummary?.text || activity.basicActivity.description}</BodyText>

            {hasRatingOrReviews && (
              <>
                <Divider kind="primary" />
                <ExploreReviews
                  rating={activity.googlePlaceDetails.rating}
                  ratingsCount={activity.googlePlaceDetails.userRatingCount}
                  reviews={activity.googlePlaceDetails.reviews}
                />
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Replace item',
          onClick: handleReplace,
          startIcon: <LineRefreshIcon />,
        }}
      />
    </>
  )
}

export default RegenerateItemDetails
